import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Chip,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from "@mui/material";
import { DISABLE_SWIPE } from "../utilities/enums";
import { FindFlightById, hasBagsLoadedData, splitFlightCode } from "../helpers";
import {
  applyLargeGroupFilter,
  buildLargeGroups,
  getGroupColor,
} from "../helpers/largeGroups";
import {
  REWARD_BENEFIT_KEYS,
  SHOW_PRIORITY_BOARDING_FILTER,
} from "../helpers/rewardTier";
import { useDataServiceContext, usePassengerFiltering } from "../hooks";

import {
  FlightInfoBox,
  FilterCard,
  ListView,
  PassengerModal,
  SeatDetails,
  ConnectionsTab,
} from "../components";
import { buildConnectionsFromPassengerList } from "../utilities/connectionHelpers/buildConnectionsFromPassengerList";
import { CrewInfo } from "./CrewInfo";

export const SeatMap = ({
  passengerList = [],
  seatmap = [],
  flight,
  dtSTD,
  airportList,
  crewList,
  bagList,
}) => {
  const SEATMAP = "SEATMAP";
  const SEATMAP_ROTATION_KEY = "SEATMAP_ROTATION";
  const LIST = "LIST";
  const CONNECTIONS = "CONNECTIONS";
  const WIDTH = 750;

  // Compress view (hide empty rows)
  const COMPRESS_VIEW_KEY = "COMPRESS_VIEW";

  const DEFAULT_FILTER = {
    statusFilter: "ALL",
    SPECIAL_NEEDS: false,
    specialNeedsCategories: [],
    ID_STAFF: false,
    OTHER_INFO: false,
    inboundConnections: false,
    outboundConnections: false,
    selectedMeals: [], // Default: no meal filters selected
    BAGS_CHECKED_IN: false,
    BAGS_LOADED: false,
    rewardTiers: [],
    rewardBenefits: [],
    showRewardStatus: false,
    LARGE_GROUPS: false,
    selectedGroup: null,
  };

  const migrateSavedFilter = (saved = {}) => {
    const merged = { ...DEFAULT_FILTER, ...saved };
    if (!Array.isArray(merged.rewardTiers)) {
      merged.rewardTiers = [];
    } else {
      merged.rewardTiers = merged.rewardTiers.filter((key) => key !== "priority");
    }
    if (!Array.isArray(merged.rewardBenefits)) {
      merged.rewardBenefits = [];
    }
    if (typeof merged.selectedGroup !== "string") {
      merged.selectedGroup = null;
    }
    merged.showRewardStatus = merged.showRewardStatus === true;
    delete merged.showRewardStatusManual;
    if (!merged.showRewardStatus) {
      merged.rewardTiers = [];
      merged.rewardBenefits = [];
    }
    if (!SHOW_PRIORITY_BOARDING_FILTER) {
      merged.rewardBenefits = merged.rewardBenefits.filter(
        (key) => key !== REWARD_BENEFIT_KEYS.PRIORITY_BOARDING
      );
    }
    delete merged.PRIORITY;
    if (!Array.isArray(merged.specialNeedsCategories)) {
      merged.specialNeedsCategories = [];
    }
    if (!merged.SPECIAL_NEEDS) {
      merged.specialNeedsCategories = [];
    }
    return merged;
  };

  const FILTER_STORAGE_KEY_PREFIX = "SEATMAP_FILTER_";

  const [isPassengerModalOpen, setIsPassengerModalOpen] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState({});
  const [selectedFilter, setSelectedFilter] = useState(() => {
    try {
      const key = `${FILTER_STORAGE_KEY_PREFIX}${flight?.flightId || ""}`;
      const saved = localStorage.getItem(key);
      return saved
        ? migrateSavedFilter(JSON.parse(saved))
        : DEFAULT_FILTER;
    } catch (e) {
      return DEFAULT_FILTER;
    }
  });

  const bagLookup = useMemo(() => {
    const byPassengerId = new Map();
    const byBookingRef = new Map();
    const byOwnerRefId = new Map();
    const bags = bagList?.bags;

    const normalizeRefId = (ref) => {
      if (typeof ref !== "string") return null;
      const parts = ref.split("/");
      return parts[parts.length - 1] || ref;
    };

    if (!Array.isArray(bags)) {
      return { byPassengerId, byBookingRef, byOwnerRefId, normalizeRefId };
    }

    const addToMap = (map, key, bag) => {
      if (!key) return;
      const current = map.get(key) || {
        bags: [],
        checkedInCount: 0,
        loadedCount: 0,
      };
      current.bags.push(bag);
      if (bag.checkedIn) current.checkedInCount += 1;
      if (bag.loaded) current.loadedCount += 1;
      map.set(key, current);
    };

    for (const bag of bags) {
      const normalizedBag = {
        id: bag?.id ?? null,
        tagNumber: bag?.licensePlate ?? bag?.tagNumber ?? null,
        licensePlate: bag?.licensePlate ?? null,
        loadingStatus: bag?.loadingStatus ?? null,
        activationStatus: bag?.activationStatus ?? null,
        finalDestination: bag?.finalDestination ?? null,
        checkedIn:
          typeof bag?.checkedIn === "boolean"
            ? bag.checkedIn
            : bag?.activationStatus === "ACTIVE",
        loaded:
          typeof bag?.loaded === "boolean"
            ? bag.loaded
            : bag?.loadingStatus === "LOADED",
        ownerRef: bag?.ownerRef ?? null,
        ownerRefId: bag?.ownerRefId ?? normalizeRefId(bag?.ownerRef),
        ownerRecordLocator: bag?.ownerRecordLocator ?? null,
      };

      addToMap(byPassengerId, bag?.ownerId, normalizedBag);
      addToMap(byOwnerRefId, normalizedBag.ownerRefId, normalizedBag);
      addToMap(byBookingRef, normalizedBag.ownerRecordLocator, normalizedBag);
    }

    return { byPassengerId, byBookingRef, byOwnerRefId, normalizeRefId };
  }, [bagList]);

  const getPassengerBagInfo = useCallback(
    (passenger) => {
      if (!passenger) return null;

      const passengerId = passenger.id || null;
      const passengerIdNormalized = bagLookup.normalizeRefId
        ? bagLookup.normalizeRefId(passengerId)
        : passengerId;

      const byId = bagLookup.byPassengerId.get(passengerId);
      const byOwnerRef = passengerIdNormalized
        ? bagLookup.byOwnerRefId.get(passengerIdNormalized)
        : null;
      const bookingRef =
        passenger.customerBookingReference || passenger.recordLocator || null;
      const byBookingRef = bookingRef
        ? bagLookup.byBookingRef.get(bookingRef)
        : null;

      const match = byId || byOwnerRef || byBookingRef;

      if (!match) return null;

      return {
        ...match,
        source: byId ? "passenger" : byOwnerRef ? "ref" : "pnr",
      };
    },
    [bagLookup]
  );

  const bagFilterStats = useMemo(() => {
    const stats = {
      checkedInPassengers: 0,
      loadedPassengers: 0,
      matchedCheckedInBags: 0,
      matchedLoadedBags: 0,
      totalCheckedInBags: bagList?.checkedInCount ?? 0,
      totalLoadedBags: bagList?.loadedCount ?? 0,
    };

    if (!Array.isArray(passengerList)) {
      return stats;
    }

    for (const passenger of passengerList) {
      const bagInfo = getPassengerBagInfo(passenger);
      if (bagInfo?.checkedInCount > 0) {
        stats.checkedInPassengers += 1;
        stats.matchedCheckedInBags += bagInfo.checkedInCount;
      }
      if (bagInfo?.loadedCount > 0) {
        stats.loadedPassengers += 1;
        stats.matchedLoadedBags += bagInfo.loadedCount;
      }
    }

    if (
      process.env.NODE_ENV !== "production" &&
      (stats.matchedCheckedInBags !== stats.totalCheckedInBags ||
        stats.matchedLoadedBags !== stats.totalLoadedBags)
    ) {
      // eslint-disable-next-line no-console
      console.warn("Bag stats mismatch", {
        matchedCheckedInBags: stats.matchedCheckedInBags,
        totalCheckedInBags: stats.totalCheckedInBags,
        matchedLoadedBags: stats.matchedLoadedBags,
        totalLoadedBags: stats.totalLoadedBags,
      });
    }

    return stats;
  }, [passengerList, getPassengerBagInfo, bagList]);

  const hasLoadedBagData = hasBagsLoadedData(bagList);

  // Use custom hook for filtering
  const filteredPassengerList = usePassengerFiltering(
    passengerList,
    selectedFilter,
    getPassengerBagInfo,
    hasLoadedBagData
  );
  const [viewMode, setViewMode] = useState(SEATMAP);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const [mealModeOn, setMealModeOn] = useState(false);
  const [isSeatMapRotated, setIsSeatMapRotated] = useState(false);

  // Load compress view preference on mount
  const [compressView, setCompressView] = useState(false);

  const { refreshSeatmapData, getMealModeData, saveMealModeData } =
    useDataServiceContext();

  const [passengersWithMeal, setPassengersWithMeal] = useState([]);

  // Track when meal save is in progress to prevent race conditions
  const mealSaveInProgressRef = useRef(false);

  const dataUnavailableMessage =
    (passengerList &&
      passengerList.dataUnavailableInAmadeus &&
      passengerList.message) ||
    (seatmap && seatmap.dataUnavailableInAmadeus && seatmap.message) ||
    null;

  const { flightId, allFlights } = flight;
  const selectedFlight = FindFlightById(allFlights, flightId);

  const handleToggle = (event, mode) => {
    if (mode !== null) {
      setViewMode(mode);
    }
  };


  const handleClosePassengerModal = () => setIsPassengerModalOpen(false);
  const handleOpenPassengerModal = () => setIsPassengerModalOpen(true);

  const handleModalOpen = (passenger) => {
    const bagInfo = getPassengerBagInfo(passenger);
    setSelectedPassenger({ ...passenger, bagInfo });
    handleOpenPassengerModal();
  };

  const handleMealModeSelection = async (selectedPassenger) => {
    mealSaveInProgressRef.current = true;

    const updatedList = passengersWithMeal.map((passenger) =>
      passenger.id === selectedPassenger.id
        ? { ...passenger, mealReceived: !passenger.mealReceived }
        : passenger
    );
    setPassengersWithMeal(updatedList);

    try {
    await saveMealModeData(flight.flightId, updatedList, [
      selectedPassenger.id,
    ]);
    } finally {
      // Wait a bit to ensure IndexedDB write is fully committed
      setTimeout(() => {
        mealSaveInProgressRef.current = false;
      }, 100);
    }
  };

  const mergePassengerWithSeatmap = (pList, seatmap) => {
    if (!Array.isArray(seatmap)) {
      return [];
    }

    const passengerMap = pList.reduce((acc, passenger) => {
      acc[passenger.id] = passenger;
      return acc;
    }, {});

    const updatedSeatmap = seatmap.map((seat) => {
      if (seat.passengerId && passengerMap[seat.passengerId]) {
        return {
          ...seat,
          passenger: passengerMap[seat.passengerId],
        };
      }
      return seat;
    });

    return updatedSeatmap;
  };

  const handleRefreshSeatmapData = async () => {
    if (!selectedFlight) {
      console.error("Cannot refresh seatmap data: selectedFlight is undefined");
      return;
    }

    const { flightNumber } = flight;
    const splitFlightCodeArray = splitFlightCode(flightNumber);

    setIsLoading(true);

    // For multi-leg flights, finalDestination is the last destination in the array
    const finalDestination =
      Array.isArray(selectedFlight?.sDest) && selectedFlight.sDest.length > 0
        ? selectedFlight.sDest[selectedFlight.sDest.length - 1]
        : null;

    await refreshSeatmapData(
      selectedFlight.id,
      splitFlightCodeArray?.flightPrefix,
      splitFlightCodeArray?.flightNumber,
      selectedFlight.flightDate,
      selectedFlight?.sDep[0],
      selectedFlight?.sDep[0],
      selectedFlight?.sDest[0],
      selectedFlight?.sSTD[0],
      selectedFlight?.sSTA[0],
      finalDestination
    );

    setIsLoading(false);
    setRefreshCounter((prev) => prev + 1);
  };

  // Persist filter per flight and rehydrate on flight change
  useEffect(() => {
    const key = `${FILTER_STORAGE_KEY_PREFIX}${flight?.flightId || ""}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        setSelectedFilter(migrateSavedFilter(JSON.parse(saved)));
      } else {
        setSelectedFilter(DEFAULT_FILTER);
      }
    } catch (e) {
      console.error(e);
    }
  }, [flight?.flightId]);

  useEffect(() => {
    const key = `${FILTER_STORAGE_KEY_PREFIX}${flight?.flightId || ""}`;
    try {
      localStorage.setItem(key, JSON.stringify(selectedFilter));
    } catch (e) {
      console.error(e);
    }
  }, [selectedFilter, flight?.flightId]);


  useEffect(() => {
    (async () => {
      // Don't reload from IndexedDB if a save is in progress
      if (mealSaveInProgressRef.current) {
        return;
      }

      const savedMealData = (await getMealModeData(flight.flightId)) || [];
      const mealMap = new Map(
        savedMealData.map((passenger) => [passenger.id, passenger.mealReceived])
      );

      if (!Array.isArray(passengerList)) {
        setPassengersWithMeal([]);
        return;
      }

      const withMeal = passengerList
        .filter((passenger) => passenger.mealObject)
          .map((passenger) => ({
            ...passenger,
            mealReceived: mealMap.get(passenger.id) ?? false,
        }));

      setPassengersWithMeal(withMeal);
    })();
  }, [passengerList, flight.flightId]);

  const filteredPassengersWithMeal = usePassengerFiltering(
    passengersWithMeal,
    selectedFilter,
    getPassengerBagInfo,
    hasLoadedBagData
  );

  const listPassengerList = useMemo(
    () =>
      applyLargeGroupFilter(
        filteredPassengerList,
        selectedFilter,
        passengerList
      ),
    [filteredPassengerList, selectedFilter, passengerList]
  );

  const listPassengersWithMeal = useMemo(
    () =>
      applyLargeGroupFilter(
        filteredPassengersWithMeal,
        selectedFilter,
        passengerList
      ),
    [filteredPassengersWithMeal, selectedFilter, passengerList]
  );

  const largeGroups = useMemo(
    () => buildLargeGroups(passengerList),
    [passengerList]
  );

  const handleGroupSelect = (groupKey) => {
    setSelectedFilter((prev) => {
      const current = migrateSavedFilter(prev);
      return {
        ...current,
        LARGE_GROUPS: true,
        selectedGroup: current.selectedGroup === groupKey ? null : groupKey,
      };
    });
  };

  const seatMapPassengerList = useMemo(
    () =>
      applyLargeGroupFilter(
        filteredPassengerList,
        { ...selectedFilter, selectedGroup: null },
        passengerList
      ),
    [filteredPassengerList, selectedFilter, passengerList]
  );

  const seatMapPassengersWithMeal = useMemo(
    () =>
      applyLargeGroupFilter(
        filteredPassengersWithMeal,
        { ...selectedFilter, selectedGroup: null },
        passengerList
      ),
    [filteredPassengersWithMeal, selectedFilter, passengerList]
  );

  // Set meal mode on
  useEffect(() => {
    const selectedMeals = selectedFilter.selectedMeals || [];
    setMealModeOn(selectedMeals.length > 0);
  }, [selectedFilter]);

  const currentPassengerList = mealModeOn
    ? seatMapPassengersWithMeal
    : seatMapPassengerList;

  const merged = mergePassengerWithSeatmap(currentPassengerList, seatmap);

  // Passengers that match current filters but do not have an assigned seat
  const passengerIdsWithSeat = new Set(
    Array.isArray(seatmap)
      ? seatmap.filter((s) => !!s.passengerId).map((s) => s.passengerId)
      : []
  );
  const unseatedCount = Array.isArray(currentPassengerList)
    ? currentPassengerList.filter((p) => !passengerIdsWithSeat.has(p.id)).length
    : 0;

  useEffect(() => {
    const storedRotation = localStorage.getItem(SEATMAP_ROTATION_KEY);
    if (storedRotation === "true") {
      setIsSeatMapRotated(true);
    }
  }, []);

  const handleRotateSeatMap = () => {
    setIsSeatMapRotated((prev) => {
      const next = !prev;
      localStorage.setItem(SEATMAP_ROTATION_KEY, next.toString());
      return next;
    });
  };

  useEffect(() => {
    const stored = localStorage.getItem(COMPRESS_VIEW_KEY);
    if (stored === "true") {
      setCompressView(true);
    }
  }, []);

  const handleCompressViewToggle = (eventOrValue) => {
    const next =
      typeof eventOrValue === "boolean"
        ? eventOrValue
        : eventOrValue.target.checked;
    setCompressView(next);
    localStorage.setItem(COMPRESS_VIEW_KEY, next.toString());
  };

  // Handle case where flight data is unavailable due to network issues or missing flights
  if (!selectedFlight) {
    return (
      <Box sx={{ width: "100%", height: "100%", padding: 3 }}>
        <Paper
          elevation={3}
          sx={{
            padding: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography variant="h6" align="center" color="error" gutterBottom>
            Flight not found
          </Typography>
          <Typography variant="body1" align="center">
            The selected flight could not be found. This may be due to a network
            error or the flight data being unavailable.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (dataUnavailableMessage) {
    return (
      <Box sx={{ width: "100%", height: "100%", padding: 3 }}>
        <Paper
          elevation={3}
          sx={{
            padding: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography variant="h6" align="center" color="error" gutterBottom>
            Data in Amadeus is not available
          </Typography>
          <Typography variant="body1" align="center">
            {dataUnavailableMessage}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <div style={{ backgroundColor: "#F1F1F1" }}>
      <Box className={"content-container"} style={{ paddingBottom: 0 }}>
        <Typography variant="h6" gutterBottom textAlign="center">
          SEAT MAP
        </Typography>
      </Box>

      {dtSTD && airportList && crewList && (
        <CrewInfo dtSTD={dtSTD} airportList={airportList} crewList={crewList} />
      )}

      <div className={DISABLE_SWIPE}>
        <Grid container style={{ zIndex: 999 }}>
        <PassengerModal
          isOpen={!!isPassengerModalOpen}
          handleClose={handleClosePassengerModal}
          selectedPassenger={selectedPassenger}
            bagInfo={
              selectedPassenger?.bagInfo ||
              getPassengerBagInfo(selectedPassenger) ||
              null
            }
        />
        </Grid>

        <Grid
          container
          direction="column"
          alignItems="stretch"
          spacing={2}
          marginY={4}
          sx={{
            maxWidth: `${WIDTH}px`,
            width: "100%",
            marginX: "auto",
            paddingX: 2,
          }}
        >
          <Grid item>
            <Typography
              variant="body1"
              component="span"
              sx={{ fontWeight: "bold" }}
            >
              {"Flight info:"}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          direction="column"
          alignItems="stretch"
          spacing={2}
          marginY={4}
          sx={{
            maxWidth: `${WIDTH}px`,
            width: "100%",
            marginX: "auto",
            paddingX: 2,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Grid item>
            <FlightInfoBox
              flight={flight}
              passengerList={passengerList}
              refreshSeatmapData={handleRefreshSeatmapData}
              isLoading={isLoading}
              bagList={bagList}
              showRotateButton={viewMode === SEATMAP}
              onRotateSeatMap={handleRotateSeatMap}
              isSeatMapRotated={isSeatMapRotated}
            />
          </Grid>
        </Grid>

        <Grid
          container
          direction="column"
          alignItems="stretch"
          spacing={2}
          marginY={4}
          sx={{
            maxWidth: `${WIDTH}px`,
            width: "100%",
            marginX: "auto",
            paddingX: 2,
          }}
        >
          <Grid item>
            <Typography
              variant="body1"
              component="span"
              sx={{ fontWeight: "bold" }}
            >
              {"Filter:"}
            </Typography>
          </Grid>
          <Grid item sx={{ width: "100%" }}>
            <FilterCard
              originalPassengerList={passengerList}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              bagFilterStats={bagFilterStats}
              compressView={compressView}
              setCompressView={handleCompressViewToggle}
            />
          </Grid>
        </Grid>

        <Grid
          container
          direction="column"
          alignItems="stretch"
          overflow={"hidden"}
          spacing={2}
          marginY={4}
          sx={{
            maxWidth: `${WIDTH}px`,
            width: "100%",
            marginX: "auto",
            paddingX: 2,
          }}
        >
          <Grid
            container
            columnSpacing={1}
            alignItems="center"
            justifyContent="center"
          >
          <Grid item>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleToggle}
            >
              <ToggleButton value={SEATMAP}>Seatmap</ToggleButton>
              <ToggleButton value={LIST}>List</ToggleButton>
                <ToggleButton value={CONNECTIONS}>Connections</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
            {viewMode === SEATMAP && unseatedCount > 0 && (
              <Grid item style={{marginLeft: 10}}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {unseatedCount} passenger{unseatedCount === 1 ? "" : "s"}{" "}
                  match the filter but have no seat assigned and won't appear on
                  the seatmap.
                </Typography>
              </Grid>
            )}
          </Grid>
          {viewMode === SEATMAP &&
            selectedFilter.LARGE_GROUPS &&
            largeGroups.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 0.75,
                }}
              >
                {largeGroups.map(({ key, label, count }) => {
                  const selected = selectedFilter.selectedGroup === key;
                  const color = getGroupColor(key, largeGroups);
                  return (
                    <Chip
                      key={key}
                      size="small"
                      variant="outlined"
                      onClick={() => handleGroupSelect(key)}
                      label={`${label} (${count})`}
                      icon={
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: color,
                            ml: "8px !important",
                          }}
                        />
                      }
                      sx={{
                        bgcolor: selected
                          ? "action.selected"
                          : "background.paper",
                        fontWeight: selected ? 700 : 400,
                      }}
                    />
                  );
                })}
              </Box>
            )}
          {viewMode === CONNECTIONS ? (
            <Grid item sx={{ marginBottom: 4, width: "100%" }}>
              <ConnectionsTab
                connections={buildConnectionsFromPassengerList(
                  currentPassengerList
                )}
                selectedFlight={selectedFlight}
              />
            </Grid>
          ) : mealModeOn ? (
            <Grid item sx={{ marginBottom: 4, width: "100%" }}>
              {viewMode === LIST ? (
                <ListView
                  list={listPassengersWithMeal}
                  handleModalOpen={handleMealModeSelection}
                  mealModeOn={mealModeOn}
                />
              ) : (
                <SeatDetails
                  seatingDetails={merged}
                  filteredPassengerList={seatMapPassengersWithMeal}
                  actualPassengers={passengerList}
                  handleModalOpen={handleMealModeSelection}
                  selectedFilter={selectedFilter}
                  mealModeOn={mealModeOn}
                  isRotated={isSeatMapRotated}
                  compressView={compressView}
                  getPassengerBagInfo={getPassengerBagInfo}
                />
              )}
            </Grid>
          ) : (
            <Grid item sx={{ marginBottom: 4, width: "100%" }}>
            {viewMode === LIST ? (
              <ListView
                  list={listPassengerList}
                handleModalOpen={handleModalOpen}
                  mealModeOn={mealModeOn}
              />
            ) : (
              <SeatDetails
                seatingDetails={merged}
                  filteredPassengerList={seatMapPassengerList}
                  actualPassengers={passengerList}
                handleModalOpen={handleModalOpen}
                selectedFilter={selectedFilter}
                  mealModeOn={mealModeOn}
                  isRotated={isSeatMapRotated}
                  compressView={compressView}
                  getPassengerBagInfo={getPassengerBagInfo}
              />
            )}
          </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};
